const LOCK_KEY = 'ob_refresh_lock';
const LOCK_TTL_MS = 12_000;

interface BroadcastMessage {
    type: 'REFRESH_DONE' | 'REFRESH_FAILED';
    accessToken?: string;
    error?: string;
}

type Waiter = {
    resolve: (msg: BroadcastMessage) => void;
    reject: (err: Error) => void;
    timeoutId: ReturnType<typeof setTimeout>;
};

class RefreshCoordinator {
    private readonly tabId = Math.random().toString(36).slice(2);

    private readonly channel: BroadcastChannel | null;

    private waiters: Waiter[] = [];

    constructor() {
        this.channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('ob_token_refresh') : null;

        if (this.channel) {
            this.channel.onmessage = ({ data }: MessageEvent<BroadcastMessage>) => {
                const current = this.waiters;
                this.waiters = [];
                if (data.type === 'REFRESH_DONE') {
                    current.forEach((w) => {
                        clearTimeout(w.timeoutId);
                        w.resolve(data);
                    });
                } else {
                    const err = new Error(data.error ?? 'Refresh failed in another tab');
                    current.forEach((w) => {
                        clearTimeout(w.timeoutId);
                        w.reject(err);
                    });
                }
            };
        }
    }

    isAnotherTabRefreshing(): boolean {
        const raw = localStorage.getItem(LOCK_KEY);
        if (!raw) return false;
        const ts = parseInt(raw.split(':')[1] ?? '0', 10);
        return Date.now() - ts < LOCK_TTL_MS;
    }

    acquireLock(): void {
        localStorage.setItem(LOCK_KEY, `${this.tabId}:${Date.now()}`);
    }

    releaseLock(): void {
        const raw = localStorage.getItem(LOCK_KEY);
        if (raw?.startsWith(this.tabId)) {
            localStorage.removeItem(LOCK_KEY);
        }
    }

    broadcastDone(accessToken?: string): void {
        this.channel?.postMessage({ type: 'REFRESH_DONE', accessToken } as BroadcastMessage);
    }

    broadcastFailed(error: string): void {
        this.channel?.postMessage({ type: 'REFRESH_FAILED', error } as BroadcastMessage);
    }

    // Wait for the tab that owns the lock to finish and broadcast its result.
    // If the broadcast never arrives (tab crash / network hang), the timeout fires
    // and the waiter rejects so the caller can retry independently.
    waitForBroadcast(): Promise<BroadcastMessage> {
        return new Promise<BroadcastMessage>((resolve, reject) => {
            const timeoutId = setTimeout(() => {
                this.waiters = this.waiters.filter((w) => w.resolve !== resolve);
                reject(new Error('cross-tab refresh timed out'));
            }, LOCK_TTL_MS + 2_000);
            this.waiters.push({ resolve, reject, timeoutId });
        });
    }
}

export const refreshCoordinator = new RefreshCoordinator();
